// index.ts
import { bearing } from "@turf/bearing";
import { centroid } from "@turf/centroid";
import { destination } from "@turf/destination";
import { featureCollection, lineString, point } from "@turf/helpers";
import { getCoord } from "@turf/invariant";
import { length } from "@turf/length";
import { featureEach, segmentEach, segmentReduce } from "@turf/meta";
function directionalMean(lines, options = {}) {
  var _a;
  const isPlanar = !!options.planar;
  const isSegment = (_a = options.segment) != null ? _a : false;
  let sigmaSin = 0;
  let sigmaCos = 0;
  let countOfLines = 0;
  let sumOfLen = 0;
  const centroidList = [];
  if (isSegment) {
    segmentEach(lines, (currentSegment) => {
      const [sin1, cos1] = getCosAndSin(
        currentSegment.geometry.coordinates,
        isPlanar
      );
      const lenOfLine = getLengthOfLineString(currentSegment, isPlanar);
      if (isNaN(sin1) || isNaN(cos1)) {
        return;
      } else {
        sigmaSin += sin1;
        sigmaCos += cos1;
        countOfLines += 1;
        sumOfLen += lenOfLine;
        centroidList.push(centroid(currentSegment));
      }
    });
  } else {
    featureEach(lines, (currentFeature) => {
      if (currentFeature.geometry.type !== "LineString") {
        throw new Error("shold to support MultiLineString?");
      }
      const [sin1, cos1] = getCosAndSin(
        currentFeature.geometry.coordinates,
        isPlanar
      );
      const lenOfLine = getLengthOfLineString(currentFeature, isPlanar);
      if (isNaN(sin1) || isNaN(cos1)) {
        return;
      } else {
        sigmaSin += sin1;
        sigmaCos += cos1;
        countOfLines += 1;
        sumOfLen += lenOfLine;
        centroidList.push(centroid(currentFeature));
      }
    });
  }
  const cartesianAngle = getAngleBySinAndCos(sigmaSin, sigmaCos);
  const bearingAngle = bearingToCartesian(cartesianAngle);
  const circularVariance = getCircularVariance(
    sigmaSin,
    sigmaCos,
    countOfLines
  );
  const averageLength = sumOfLen / countOfLines;
  const centroidOfLines = centroid(featureCollection(centroidList));
  const [averageX, averageY] = getCoord(centroidOfLines);
  let meanLinestring;
  if (isPlanar) {
    meanLinestring = getMeanLineString(
      [averageX, averageY],
      cartesianAngle,
      averageLength,
      isPlanar
    );
  } else {
    meanLinestring = getMeanLineString(
      [averageX, averageY],
      bearingAngle,
      averageLength,
      isPlanar
    );
  }
  return lineString(meanLinestring, {
    averageLength,
    averageX,
    averageY,
    bearingAngle,
    cartesianAngle,
    circularVariance,
    countOfLines
  });
}
function euclideanDistance(coords) {
  const [x0, y0] = coords[0];
  const [x1, y1] = coords[1];
  const dx = x1 - x0;
  const dy = y1 - y0;
  return Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));
}
function getLengthOfLineString(line, isPlanar) {
  if (isPlanar) {
    return segmentReduce(
      line,
      (previousValue, segment) => {
        const coords = segment.geometry.coordinates;
        return previousValue + euclideanDistance(coords);
      },
      0
    );
  } else {
    return length(line, {
      units: "meters"
    });
  }
}
function bearingToCartesian(angle) {
  let result = 90 - angle;
  if (result > 180) {
    result -= 360;
  }
  return result;
}
function getCosAndSin(coordinates, isPlanar) {
  const beginPoint = coordinates[0];
  const endPoint = coordinates[coordinates.length - 1];
  if (isPlanar) {
    const [x0, y0] = beginPoint;
    const [x1, y1] = endPoint;
    const dx = x1 - x0;
    const dy = y1 - y0;
    const h = Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));
    if (h < 1e-9) {
      return [NaN, NaN];
    }
    const sin1 = dy / h;
    const cos1 = dx / h;
    return [sin1, cos1];
  } else {
    const angle = bearingToCartesian(bearing(beginPoint, endPoint));
    const radian = angle * Math.PI / 180;
    return [Math.sin(radian), Math.cos(radian)];
  }
}
function getAngleBySinAndCos(sin1, cos1) {
  let angle = 0;
  if (Math.abs(cos1) < 1e-9) {
    angle = 90;
  } else {
    angle = Math.atan2(sin1, cos1) * 180 / Math.PI;
  }
  if (sin1 >= 0) {
    if (cos1 < 0) {
      angle += 180;
    }
  } else {
    if (cos1 < 0) {
      angle -= 180;
    }
  }
  return angle;
}
function getCircularVariance(sin1, cos1, len) {
  if (len === 0) {
    throw new Error("the size of the features set must be greater than 0");
  }
  return 1 - Math.sqrt(Math.pow(sin1, 2) + Math.pow(cos1, 2)) / len;
}
function getMeanLineString(centroidOfLine, angle, lenOfLine, isPlanar) {
  if (isPlanar) {
    const [averageX, averageY] = centroidOfLine;
    let beginX;
    let beginY;
    let endX;
    let endY;
    const r = angle * Math.PI / 180;
    const sin = Math.sin(r);
    const cos = Math.cos(r);
    beginX = averageX - lenOfLine / 2 * cos;
    beginY = averageY - lenOfLine / 2 * sin;
    endX = averageX + lenOfLine / 2 * cos;
    endY = averageY + lenOfLine / 2 * sin;
    return [
      [beginX, beginY],
      [endX, endY]
    ];
  } else {
    const end = destination(point(centroidOfLine), lenOfLine / 2, angle, {
      units: "meters"
    });
    const begin = destination(point(centroidOfLine), -lenOfLine / 2, angle, {
      units: "meters"
    });
    return [getCoord(begin), getCoord(end)];
  }
}
var turf_directional_mean_default = directionalMean;
export {
  turf_directional_mean_default as default,
  directionalMean
};
//# sourceMappingURL=index.js.map